import React from "react";
import { Button, Dropdown, Icon } from "semantic-ui-react";
import { PropertyPhotoOrIcon } from "./PropertyPhoto";

// A previously-considered Buyer property, collapsed by default so a
// transaction with several properties doesn't turn into an endlessly long
// page. Expanding reveals the property's full StrategyPropertyDashboard
// (photo header + divider + strategy cards) in place, via `expanded`/
// `onToggle` - state lives in the parent (BuyerStrategyPropertyRows) so it
// can be reset/driven consistently with the rest of that component's state,
// per this app's existing convention of not introducing persistent
// expand/collapse state beyond a component's own lifetime unless the page
// already does so elsewhere (it doesn't).
export default function CompactBuyerPropertyRow({
  pic,
  street,
  unit,
  city,
  state,
  facts,
  lastListPrice,
  latestOfferSummary,
  expanded,
  onToggle,
  onSetCurrent,
  isMobile,
  children,
}) {
  const cityState = [city, state].filter(Boolean).join(", ");
  const panelId = `buyer-property-panel-${street || "property"}`.replace(/\s+/g, "-");

  return (
    <div style={{ border: "1px solid #e0e1e2", borderRadius: "8px", padding: "12px 14px", background: "#ffffff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <PropertyPhotoOrIcon pic={pic} size={44} />

        <div style={{ flex: "1 1 auto", minWidth: 0 }}>
          <b style={{ fontSize: "0.95em" }}>{street}{unit ? ` ${unit}` : ""}</b>
          <div className="text-grey" style={{ fontSize: "0.82em" }}>
            {[cityState, facts && facts.length > 0 ? facts.join(" · ") : null].filter(Boolean).join(" — ")}
          </div>
        </div>

        {!isMobile && (
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            {lastListPrice && (
              <div style={{ fontSize: "0.85em" }}>
                <span className="text-grey">List: </span>
                <span style={{ fontWeight: 600 }}>{lastListPrice}</span>
              </div>
            )}
            {latestOfferSummary && (
              <div style={{ fontSize: "0.8em", color: "#1e8e3e", fontWeight: 600 }}>{latestOfferSummary}</div>
            )}
          </div>
        )}

        <Button
          basic
          icon
          size="small"
          aria-expanded={expanded}
          aria-controls={panelId}
          aria-label={expanded ? `Collapse ${street}` : `Expand ${street}`}
          onClick={onToggle}
        >
          <Icon name={expanded ? "chevron up" : "chevron down"} aria-hidden="true" />
        </Button>

        <Dropdown icon="ellipsis vertical" button basic className="icon" floating direction="left" aria-label={`More actions for ${street}`}>
          <Dropdown.Menu>
            <Dropdown.Item text="Set as Current Property" onClick={onSetCurrent} />
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {isMobile && (lastListPrice || latestOfferSummary) && (
        <div style={{ marginTop: "8px", fontSize: "0.85em" }}>
          {lastListPrice && <span><span className="text-grey">List: </span><span style={{ fontWeight: 600 }}>{lastListPrice}</span></span>}
          {lastListPrice && latestOfferSummary && " · "}
          {latestOfferSummary && <span style={{ color: "#1e8e3e", fontWeight: 600 }}>{latestOfferSummary}</span>}
        </div>
      )}

      {expanded && (
        <div id={panelId} role="region" aria-label={`${street} Strategies`} style={{ marginTop: "14px" }}>
          {children}
        </div>
      )}
    </div>
  );
}
